// src/components/ReportDisplay.js
import React, { useState } from 'react'
import { 
  Download, Mail, Printer, Share2, CheckCircle, AlertCircle, 
  TrendingUp, Target, Award, Clock, BarChart3, MessageSquare,
  FileText, ChevronDown, ChevronUp
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { generateProfessionalPDF } from '../utils/enhancedPdfGenerator'
import { parseTranscript, getRawTranscript } from '../utils/transcriptParser'

const ReportDisplay = ({ 
  analysis, 
  transcript, 
  agent, 
  callType, 
  industry = "home_improvement",
  onEmailReport,
  onNewAnalysis
}) => {
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    strengths: true,
    improvements: true,
    scores: true,
    recommendations: true,
    transcript: false
  })

  // Add state for dynamic sections
  const [dynamicExpanded, setDynamicExpanded] = useState({})

  // Internal PDF handler
  const handleDownloadPDF = () => {
    const score = extractScore(analysis)
    generateProfessionalPDF({
      analysis,
      transcript,
      agent,
      callType,
      industry,
      score,
      timestamp: new Date().toLocaleString()
    })
  }

  // Parse the analysis into structured sections
  const parseAnalysis = (text) => {
    const sections = {}
    if (!text) return sections
    
    const lines = text.split('\n')
    let currentSection = null
    let currentContent = []

    lines.forEach(line => {
      // Skip the main "Analysis and Recommendations" header
      if (line.startsWith('## Analysis and Recommendations')) {
        return
      }
      // Check for main sections (##) - but we primarily use ### 
      else if (line.startsWith('## ')) {
        if (currentSection) {
          sections[currentSection] = currentContent.join('\n').trim()
        }
        currentSection = line.replace('## ', '').trim()
        currentContent = []
      }
      // Check for subsections (###) - these are our main sections
      else if (line.startsWith('### ')) {
        if (currentSection) {
          sections[currentSection] = currentContent.join('\n').trim()
        }
        currentSection = line.replace('### ', '').trim()
        currentContent = []
      }
      else {
        currentContent.push(line)
      }
    })

    // Don't forget the last section
    if (currentSection) {
      sections[currentSection] = currentContent.join('\n').trim()
    }

    return sections
  }

  // Extract score from analysis
  const extractScore = (text) => {
    const match = text.match(/Overall Score:?\s*(\d+(?:\.\d+)?)/i)
    return match ? parseFloat(match[1]) : null
  }

  // Extract key metrics
  const extractMetrics = (text) => {
    const metrics = {
      appointmentSet: false,
      readyForLive: 'Unknown',
      callType: callType || 'Unknown'
    }
    
    // Check in the full analysis text
    if (text.match(/Appointment\s*(?:Secured|Set)?:?\s*YES/i)) {
      metrics.appointmentSet = true
    }
    
    const readyMatch = text.match(/Ready\s*for\s*Live.*?:\s*(YES|NO|NEEDS\s*COACHING)/i)
    if (readyMatch) {
      metrics.readyForLive = readyMatch[1].replace(/\s+/g, ' ')
    }
    
    return metrics
  }

  const sections = parseAnalysis(analysis || '')
  const messages = parseTranscript(transcript || '') // Using imported function
  const score = extractScore(analysis || '')
  const metrics = extractMetrics(analysis || '')

  // Score color and grade
  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100'
    if (score >= 6) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getGrade = (score) => {
    if (score >= 9) return 'A+'
    if (score >= 8) return 'A'
    if (score >= 7) return 'B'
    if (score >= 6) return 'C'
    if (score >= 5) return 'D'
    return 'F'
  }

  const toggleSection = (section) => {
    // Check if it's a predefined section
    if (section in expandedSections) {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }))
    } else {
      // It's a dynamic section
      setDynamicExpanded(prev => ({
        ...prev,
        [section]: !prev[section]
      }))
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Call Analysis Report</h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {new Date().toLocaleDateString()}
              </span>
              <span className="flex items-center">
                <MessageSquare className="h-4 w-4 mr-1" />
                {callType}
              </span>
              <span className="flex items-center">
                <Target className="h-4 w-4 mr-1" />
                {agent}
              </span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button
              onClick={handleDownloadPDF}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </button>
            <button
              onClick={onEmailReport}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </button>
            <button
              onClick={onNewAnalysis}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              <FileText className="h-4 w-4 mr-2" />
              New
            </button>
          </div>
        </div>

        {/* Score Display */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="col-span-1">
            <div className={`rounded-xl p-6 text-center ${score ? getScoreColor(score) : 'bg-gray-100'}`}>
              <div className="text-4xl font-bold">{score || 'N/A'}</div>
              <div className="text-sm font-medium mt-1">Overall Score</div>
              {score && (
                <div className="text-2xl font-bold mt-2">Grade: {getGrade(score)}</div>
              )}
            </div>
          </div>
          
          <div className="col-span-3 grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Appointment</span>
                {metrics.appointmentSet ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
              </div>
              <div className="font-semibold mt-1">
                {metrics.appointmentSet ? 'Secured' : 'Not Set'}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Ready Status</div>
              <div className={`font-semibold mt-1 ${
                metrics.readyForLive === 'YES' ? 'text-green-600' :
                metrics.readyForLive === 'NO' ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                {metrics.readyForLive}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Call Type</div>
              <div className="font-semibold mt-1 capitalize">
                {metrics.callType}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Sections */}
      <div className="space-y-4">
        {/* Show raw analysis if no sections parsed */}
        {Object.keys(sections).length === 0 && analysis && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Call Analysis</h2>
              <div className="prose max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {analysis}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Executive Summary */}
        {sections['Executive Summary'] && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection('summary')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition"
            >
              <div className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-3 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Executive Summary</h2>
              </div>
              {expandedSections.summary ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.summary && (
              <div className="p-6">
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {sections['Executive Summary']}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Strengths */}
        {sections['Strengths Identified'] && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection('strengths')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition"
            >
              <div className="flex items-center">
                <Award className="h-5 w-5 mr-3 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Strengths Identified</h2>
              </div>
              {expandedSections.strengths ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.strengths && (
              <div className="p-6">
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {sections['Strengths Identified']}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Improvements */}
        {(sections['Critical Improvements Needed'] || sections['Areas for Improvement']) && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection('improvements')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 transition"
            >
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-3 text-yellow-600" />
                <h2 className="text-lg font-semibold text-gray-900">Areas for Improvement</h2>
              </div>
              {expandedSections.improvements ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.improvements && (
              <div className="p-6">
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {sections['Critical Improvements Needed'] || sections['Areas for Improvement']}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Detailed Scores */}
        {sections['Detailed Category Scores'] && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection('scores')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition"
            >
              <div className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-3 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Detailed Scores</h2>
              </div>
              {expandedSections.scores ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.scores && (
              <div className="p-6">
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {sections['Detailed Category Scores']}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Training Recommendations */}
        {sections['Training Recommendations'] && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection('recommendations')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 transition"
            >
              <div className="flex items-center">
                <Target className="h-5 w-5 mr-3 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Training Recommendations</h2>
              </div>
              {expandedSections.recommendations ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSections.recommendations && (
              <div className="p-6">
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {sections['Training Recommendations']}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Additional Sections - Display any other sections found in the analysis */}
        {Object.entries(sections).map(([title, content]) => {
          // Skip sections we've already displayed
          const displayed = [
            'Executive Summary',
            'Strengths Identified',
            'Critical Improvements Needed',
            'Areas for Improvement',
            'Detailed Category Scores',
            'Training Recommendations'
          ]
          
          if (displayed.includes(title) || !content || content.length === 0) {
            return null
          }
          
          // Special handling for score-related sections
          if (title.includes('Overall Score') || title.includes('Appointment Secured') || title.includes('Ready for Live')) {
            return null // These are already shown in the header metrics
          }
          
          return (
            <div key={title} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection(title.toLowerCase().replace(/\s+/g, '_'))}
                className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100 transition"
              >
                <div className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-3 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                </div>
                {dynamicExpanded[title.toLowerCase().replace(/\s+/g, '_')] ? <ChevronUp /> : <ChevronDown />}
              </button>
              {(dynamicExpanded[title.toLowerCase().replace(/\s+/g, '_')] !== false) && (
                <div className="p-6">
                  <div className="prose max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {content}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Transcript */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <button
            onClick={() => toggleSection('transcript')}
            className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100 transition"
          >
            <div className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-3 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Call Transcript</h2>
              <span className="ml-3 text-sm text-gray-500">({messages.length} exchanges)</span>
            </div>
            {expandedSections.transcript ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections.transcript && (
            <div className="p-6">
              <div className="max-h-96 overflow-y-auto space-y-3">
                {messages.length > 0 ? (
                  messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.type === 'agent' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-3 ${
                          msg.type === 'agent'
                            ? 'bg-blue-100 text-blue-900'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="text-xs font-semibold mb-1">
                          {msg.speaker}
                        </div>
                        <div className="text-sm">{msg.text}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No formatted transcript available</p>
                    {transcript && (
                      <div className="mt-4">
                        <p className="text-sm mb-2">Raw transcript:</p>
                        <pre className="text-left whitespace-pre-wrap text-xs bg-gray-50 p-4 rounded">
                          {getRawTranscript(transcript)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReportDisplay
